import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Save, Inbox, FileEdit, Upload, Trash2, Plus, ImageIcon } from "lucide-react";
import Logo from "../components/site/Logo";
import { API, mediaUrl } from "../lib/site";

const SECTIONS = [
  { k: "members", label: "Members", custom: true },
  { k: "concert", label: "Concert", custom: true },
  { k: "gallery", label: "Gallery", custom: true },
  { k: "videos", label: "Videos", custom: true },
  { k: "faqs", label: "FAQs" },
  { k: "venues", label: "Venues" },
];

function errText(e) {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return "Something went wrong.";
}

function UploadButton({ onUploaded, testid, label = "Upload Photo" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      onUploaded(data.url);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold text-gold transition-colors hover:bg-gold hover:text-navy-950 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {label}
      </button>
    </>
  );
}

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [content, setContent] = useState({});
  const [selKey, setSelKey] = useState("members");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/enquiries`, { withCredentials: true }).then((r) => setEnquiries(r.data)).catch(() => {});
    axios.get(`${API}/content`).then((r) => setContent(r.data || {})).catch(() => {});
  }, [user]);

  useEffect(() => {
    setDraft(content[selKey] !== undefined ? JSON.parse(JSON.stringify(content[selKey])) : null);
  }, [selKey, content]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      toast.success(`Welcome back, ${data.name}.`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  const save = async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${selKey}`, { data }, { withCredentials: true });
      setContent((c) => ({ ...c, [selKey]: data }));
      toast.success("Saved — live on the website now.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const saveJson = async () => {
    try {
      await save(JSON.parse(typeof draft === "string" ? draft : JSON.stringify(draft)));
    } catch (err) {
      if (err instanceof SyntaxError) toast.error("Invalid JSON — please check the format.");
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950 px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="tk-card gold-frame w-full max-w-sm p-8" data-testid="admin-login-form">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="mt-6 text-center font-serif text-2xl font-semibold text-ivory">Admin Login</h1>
          <p className="mt-1 text-center text-xs text-slate-500">Content management for Tricity Kroonerz</p>
          <div className="mt-6 space-y-4">
            <input type="email" required data-testid="admin-email-input" className="tk-input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input type="password" required data-testid="admin-password-input" className="tk-input" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button type="submit" disabled={busy} data-testid="admin-login-button" className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-bold text-navy-950 hover:bg-gold-light disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
            </button>
          </div>
          <Link to="/" className="mt-5 block text-center text-xs text-slate-500 hover:text-gold" data-testid="admin-back-home-link">← Back to website</Link>
        </form>
        <Toaster theme="dark" position="top-center" richColors />
      </div>
    );
  }

  const SaveBar = ({ onSave, testid }) => (
    <button onClick={onSave} disabled={saving} data-testid={testid} className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold text-navy-950 hover:bg-gold-light disabled:opacity-60">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & Publish
    </button>
  );

  const isJsonSection = !SECTIONS.find((s) => s.k === selKey)?.custom;

  return (
    <div className="min-h-screen bg-navy-950" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-navy-700 bg-navy-900/70 px-4 py-3 backdrop-blur sm:px-6">
        <Logo />
        <div className="flex items-center gap-3">
          <Link to="/" className="text-xs font-semibold text-slate-400 hover:text-gold" data-testid="admin-view-site-link">View Site</Link>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-navy-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-gold/50 hover:text-gold">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex gap-2">
          {[{ k: "enquiries", label: "Enquiries", icon: Inbox }, { k: "content", label: "Website Content", icon: FileEdit }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.k ? "bg-gold text-navy-950" : "border border-navy-700 text-slate-300 hover:border-gold/40"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.k === "enquiries" && enquiries.length > 0 && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${tab === t.k ? "bg-navy-950/20" : "bg-gold/15 text-gold"}`}>{enquiries.length}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && (
              <p className="rounded-xl border border-navy-700 bg-navy-850/60 p-8 text-center text-sm text-slate-400">
                No enquiries yet. New booking and availability requests will appear here and be emailed to tricitykroonerz@gmail.com.
              </p>
            )}
            {enquiries.map((q) => (
              <div key={q.id} className="tk-card p-5" data-testid={`admin-enquiry-${q.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-serif text-lg font-semibold text-ivory">{q.name}</p>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${q.type === "booking" ? "bg-gold/15 text-gold" : "bg-burgundy/30 text-gold-light"}`}>
                    {q.type === "booking" ? "Booking Enquiry" : "Availability Check"}
                  </span>
                </div>
                <div className="mt-3 grid gap-x-8 gap-y-1.5 text-sm text-slate-300 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(q)
                    .filter(([k, v]) => v && !["id", "type", "read", "created_at", "name"].includes(k))
                    .map(([k, v]) => (
                      <p key={k}><span className="text-slate-500">{k.replace(/_/g, " ")}: </span>{v}</p>
                    ))}
                </div>
                <p className="mt-3 text-[11px] text-slate-500">{new Date(q.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "content" && (
          <div className="mt-8" data-testid="admin-content-editor">
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map((s) => (
                <button
                  key={s.k}
                  onClick={() => setSelKey(s.k)}
                  data-testid={`admin-section-${s.k}`}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${selKey === s.k ? "bg-gold text-navy-950" : "border border-navy-700 text-slate-300 hover:border-gold/40"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {draft === null ? (
              <Loader2 className="mt-10 h-6 w-6 animate-spin text-gold" />
            ) : selKey === "members" ? (
              <div className="mt-6 space-y-4" data-testid="members-editor">
                <p className="text-xs text-slate-500">Upload each member's photo and optionally add a role or short description. Add more members anytime.</p>
                {draft.map((m, i) => (
                  <div key={i} className="tk-card flex flex-wrap items-center gap-4 p-4" data-testid={`member-edit-row-${i}`}>
                    {m.photo ? (
                      <img src={mediaUrl(m.photo)} alt={m.name} className="h-16 w-16 rounded-full border border-gold/30 object-cover" />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border border-navy-700 bg-navy-900 text-slate-500"><ImageIcon className="h-5 w-5" /></div>
                    )}
                    <div className="min-w-[200px] flex-1 space-y-2">
                      <input className="tk-input" value={m.name} placeholder="Member name" data-testid={`member-name-input-${i}`}
                        onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                      <input className="tk-input" value={m.role || ""} placeholder="Role / description (optional)" data-testid={`member-role-input-${i}`}
                        onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} />
                    </div>
                    <div className="flex items-center gap-2">
                      <UploadButton testid={`member-photo-upload-${i}`} label={m.photo ? "Change" : "Upload Photo"}
                        onUploaded={(url) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, photo: url } : x)))} />
                      <button onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} data-testid={`member-remove-${i}`} className="rounded-full border border-navy-700 p-2 text-slate-400 hover:border-burgundy hover:text-burgundy-light" aria-label="Remove member">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <button onClick={() => setDraft((d) => [...d, { name: "", role: "", photo: "" }])} data-testid="member-add-button" className="inline-flex items-center gap-1.5 rounded-full border border-navy-700 px-4 py-2 text-xs font-bold text-slate-300 hover:border-gold/50 hover:text-gold">
                  <Plus className="h-3.5 w-3.5" /> Add Member
                </button>
                <div><SaveBar onSave={() => save(draft)} testid="members-save-button" /></div>
              </div>
            ) : selKey === "concert" ? (
              <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="concert-editor">
                <div className="space-y-4">
                  <div>
                    <label className="tk-label">Status</label>
                    <select className="tk-input" value={draft.status || "upcoming"} data-testid="concert-status-select"
                      onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
                      <option value="upcoming">Upcoming</option>
                      <option value="past">Past Event</option>
                    </select>
                  </div>
                  <div>
                    <label className="tk-label">Date Label</label>
                    <input className="tk-input" value={draft.date_label || ""} data-testid="concert-date-input"
                      onChange={(e) => setDraft((d) => ({ ...d, date_label: e.target.value }))} placeholder="e.g. 15 November 2026" />
                  </div>
                  <div>
                    <label className="tk-label">Title</label>
                    <input className="tk-input" value={draft.title || ""} data-testid="concert-title-input"
                      onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
                  </div>
                  <div>
                    <label className="tk-label">Venue</label>
                    <input className="tk-input" value={draft.venue || ""} data-testid="concert-venue-input"
                      onChange={(e) => setDraft((d) => ({ ...d, venue: e.target.value }))} />
                  </div>
                  <div>
                    <label className="tk-label">Details</label>
                    <textarea rows={4} className="tk-input" value={draft.details || ""} data-testid="concert-details-input"
                      onChange={(e) => setDraft((d) => ({ ...d, details: e.target.value }))} />
                  </div>
                  <div>
                    <label className="tk-label">Ticket / Event Link (optional)</label>
                    <input className="tk-input" value={draft.ticket_link || ""} data-testid="concert-ticket-input"
                      onChange={(e) => setDraft((d) => ({ ...d, ticket_link: e.target.value }))} placeholder="https://…" />
                  </div>
                </div>
                <div>
                  <label className="tk-label">Concert Poster</label>
                  {draft.poster ? (
                    <img src={mediaUrl(draft.poster)} alt="Concert poster" className="w-full rounded-2xl border border-gold/25 object-cover" data-testid="concert-poster-preview" />
                  ) : (
                    <div className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-navy-700 text-slate-500">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-3">
                    <UploadButton testid="concert-poster-upload" label={draft.poster ? "Replace Poster" : "Upload Poster"}
                      onUploaded={(url) => setDraft((d) => ({ ...d, poster: url }))} />
                    {draft.poster && (
                      <button onClick={() => setDraft((d) => ({ ...d, poster: "" }))} data-testid="concert-poster-remove" className="text-xs font-semibold text-slate-500 hover:text-burgundy-light">Remove</button>
                    )}
                  </div>
                  <div><SaveBar onSave={() => save(draft)} testid="concert-save-button" /></div>
                </div>
              </div>
            ) : selKey === "gallery" ? (
              <div className="mt-6" data-testid="gallery-editor">
                <div className="flex flex-wrap items-center gap-3">
                  <UploadButton testid="gallery-upload-button" label="Upload New Photo"
                    onUploaded={(url) => setDraft((d) => [...d, { src: url, caption: "" }])} />
                  <p className="text-xs text-slate-500">Photos appear in the Gallery section once you save. JPG/PNG/WEBP up to 8 MB.</p>
                </div>
                {draft.length === 0 && (
                  <p className="mt-6 rounded-xl border border-navy-700 bg-navy-850/60 p-6 text-sm text-slate-400">
                    No photos yet — tasteful placeholders are shown on the website until you upload real photographs here.
                  </p>
                )}
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {draft.map((g, i) => (
                    <div key={i} className="tk-card overflow-hidden" data-testid={`gallery-edit-item-${i}`}>
                      <img src={mediaUrl(g.src)} alt={g.caption || `Gallery ${i + 1}`} className="h-44 w-full object-cover" />
                      <div className="space-y-3 p-4">
                        <input className="tk-input" value={g.caption || ""} placeholder="Caption (optional)" data-testid={`gallery-caption-input-${i}`}
                          onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))} />
                        <button onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} data-testid={`gallery-remove-${i}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-burgundy-light">
                          <Trash2 className="h-3.5 w-3.5" /> Remove photo
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {draft.length > 0 && <SaveBar onSave={() => save(draft)} testid="gallery-save-button" />}
              </div>
            ) : selKey === "videos" ? (
              <div className="mt-6 space-y-4" data-testid="videos-editor">
                <p className="text-xs text-slate-500">Paste any YouTube video link (e.g. https://www.youtube.com/watch?v=… or https://youtu.be/…) — it appears in the "Hear the Kroonerz" section once you save.</p>
                {draft.map((v, i) => (
                  <div key={i} className="tk-card flex flex-wrap items-center gap-4 p-4" data-testid={`video-edit-row-${i}`}>
                    <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-navy-700 bg-navy-900">
                      {v.id ? (
                        <img src={`https://img.youtube.com/vi/${v.id}/default.jpg`} alt={v.title || "Video"} className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-slate-500" />
                      )}
                    </div>
                    <div className="min-w-[200px] flex-1 space-y-2">
                      <input className="tk-input" value={v.url || (v.id ? `https://www.youtube.com/watch?v=${v.id}` : "")} placeholder="Paste YouTube link" data-testid={`video-url-input-${i}`}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const m = raw.match(/(?:youtube\.com\/(?:watch\?.*v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
                          setDraft((d) => d.map((x, j) => (j === i ? { ...x, url: raw, id: m ? m[1] : x.id } : x)));
                        }} />
                      <input className="tk-input" value={v.title || ""} placeholder="Title (optional, e.g. Live at DSOI)" data-testid={`video-title-input-${i}`}
                        onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                    </div>
                    <button onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} data-testid={`video-remove-${i}`} className="rounded-full border border-navy-700 p-2 text-slate-400 hover:border-burgundy hover:text-burgundy-light" aria-label="Remove video">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button onClick={() => setDraft((d) => [...d, { id: "", url: "", title: "" }])} data-testid="video-add-button" className="inline-flex items-center gap-1.5 rounded-full border border-navy-700 px-4 py-2 text-xs font-bold text-slate-300 hover:border-gold/50 hover:text-gold">
                  <Plus className="h-3.5 w-3.5" /> Add YouTube Video
                </button>
                <div><SaveBar onSave={() => save(draft.map(({ url, ...v }) => v).filter((v) => v.id))} testid="videos-save-button" /></div>
              </div>
            ) : (
              <div className="mt-6">
                <p className="text-xs leading-relaxed text-slate-500">
                  Edit the JSON for the "{selKey}" section and save — the website updates instantly.
                </p>
                <textarea
                  value={typeof draft === "string" ? draft : JSON.stringify(draft, null, 2)}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={16}
                  spellCheck={false}
                  data-testid="admin-content-textarea"
                  className="tk-input mt-4 font-mono text-xs leading-relaxed"
                />
                <SaveBar onSave={saveJson} testid="admin-content-save-button" />
              </div>
            )}
          </div>
        )}
      </div>
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}
